import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/types/drama';
import { getFeatured } from '@/utils/drama';

export default function HeroCarousel() {
  const slides: Drama[] = getFeatured();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const slide = slides[current];

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <div className="relative h-[440px] w-full md:h-[600px]">
        {slides.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              i === current ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={s.backdrop}
              alt={`${s.title} 焦点剧集海报`}
              title={`${s.title} 热门电视剧在线观看`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent"></div>

        <div className="relative mx-auto flex h-full max-w-6xl items-center px-4 md:px-6">
          <div key={slide.id} className="max-w-xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/90 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-fill"></i>
              本周焦点热剧
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-5xl">
              {slide.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-background-100">
              <span className="flex items-center gap-1 text-accent-300">
                <i className="ri-star-fill"></i>
                {slide.rating.toFixed(1)}
              </span>
              <span>{slide.region}</span>
              <span>{slide.year}</span>
              <span>全 {slide.episodes} 集</span>
            </div>
            <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-background-200 md:text-base">
              {slide.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slide.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/detail/${slide.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
              >
                <i className="ri-information-line text-lg"></i>
                剧情介绍
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setCurrent((c) => (c - 1 + slides.length) % slides.length)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/40 md:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => setCurrent((c) => (c + 1) % slides.length)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 transition-colors hover:bg-background-50/40 md:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setCurrent(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                i === current ? 'w-8 bg-primary-500' : 'w-2 bg-background-50/60'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}